import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import AddIcon from "@mui/icons-material/Add";
import HomeIcon from "@mui/icons-material/Home";
import WorkIcon from "@mui/icons-material/Work";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import getBrand from "../../brands/getBrand";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import CustomAxios from "../../utils/CustomAxios";
import { updateId } from "../../Redux/user";

const SavedAddresses = () => {
  const brand = getBrand();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.id);

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeAddressId, setActiveAddressId] = useState(null);

  const menuOpen = Boolean(menuAnchorEl);

  // Ensure userId is hydrated (similar pattern as other pages)
  useEffect(() => {
    if (!userId) {
      const storedId = localStorage.getItem("id");
      if (storedId) {
        dispatch(updateId(storedId));
      }
    }
  }, [userId, dispatch]);

  useEffect(() => {
    const fetchAddresses = async () => {
      const effectiveUserId = userId || localStorage.getItem("id");
      if (!effectiveUserId) {
        setError("User ID not found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await CustomAxios.get(`address/${effectiveUserId}`);
        setAddresses(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching addresses:", err);
        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.error ||
            "Failed to load addresses"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [userId]);

  const renderAddressIcon = (label = "") => {
    const lower = String(label).toLowerCase();
    if (lower.includes("work") || lower.includes("office")) {
      return <WorkIcon fontSize="small" />;
    }
    return <HomeIcon fontSize="small" />;
  };

  const handleMenuOpen = (event, id) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveAddressId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveAddressId(null);
  };

  const handleDeleteAddress = async () => {
    if (!activeAddressId) return;
    try {
      await CustomAxios.delete(`/addressupdate/${activeAddressId}`);
      setAddresses((prev) => prev.filter((addr) => addr.id !== activeAddressId));
    } catch (err) {
      console.error("Error deleting address:", err);
    } finally {
      handleMenuClose();
    }
  };

  const handleEditAddress = async () => {
    if (!activeAddressId) return;
    try {
      const response = await CustomAxios.get(`/addressupdate/${activeAddressId}`);
      navigate("/add-address", {
        state: { specificAddressData: response.data },
      });
    } catch (err) {
      console.error("Error fetching address for edit:", err);
    } finally {
      handleMenuClose();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: brand?.backgroundColor || "#F9FAFB",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          px: { xs: 2, md: 3 },
          background: "#fff",
          borderBottom: "1px solid #E5E7EB",
          height: { xs: "56px", md: "72px" },
        }}
      >
        <Button
          startIcon={
            <ArrowBackIosIcon
              sx={{
                fontSize: "14px",
                color: brand?.theme?.textPrimary || "#111827",
              }}
            />
          }
          onClick={() => navigate("/user_profile")}
          sx={{ textTransform: "none" }}
        />
        <Typography
          sx={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}
        >
          Addresses
        </Typography>
        <Box />
      </Box>

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Add New Address button */}
        <Card
          onClick={() => navigate("/add-address")}
          sx={{
            borderRadius: "12px",
            padding: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AddIcon
                sx={{
                  fontSize: 18,
                  color: "#fff",
                  fontWeight: 600,
                  backgroundColor: brand?.themeColor || "#2563EB",
                  borderRadius: "999px",
                  padding: "4px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              Add New Address
            </Typography>
          </Box>
        </Card>

        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: "#6B7280",
            mt: 1,
          }}
        >
          Saved Addresses
        </Typography>

        {/* Content */}
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <CircularProgress />
            <Typography
              sx={{ mt: 2, fontSize: 14, color: "#6B7280" }}
            >
              Loading addresses...
            </Typography>
          </Box>
        ) : error ? (
          <Typography
            sx={{ mt: 2, fontSize: 14, color: "#EF4444", textAlign: "center" }}
          >
            {error}
          </Typography>
        ) : addresses.length === 0 ? (
          <Typography
            sx={{ mt: 2, fontSize: 14, color: "#6B7280", textAlign: "center" }}
          >
            You don&apos;t have any saved addresses yet.
          </Typography>
        ) : (
          addresses.map((addr, index) => (
            <Card
              key={addr.id || index}
              sx={{
                borderRadius: "12px",
                padding: 2,
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, flex: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "999px",
                    background: "#F3F4F6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {renderAddressIcon(addr?.address_type || addr?.tag || addr?.label)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}
                    >
                      {addr?.address_type || "Address"}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontSize: 13, color: "#4B5563", whiteSpace: "pre-wrap" }}
                  >
                    {addr?.address_line1 || addr?.address || addr?.street || ""}
                    {addr?.city ? `, ${addr.city}` : ""}
                    {addr?.state ? `, ${addr.state}` : ""}
                    {addr?.pincode ? `, ${addr.pincode}` : ""}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, addr.id)}
              >
                <MoreVertIcon sx={{ fontSize: 18, color: "#6B7280" }} />
              </IconButton>
            </Card>
          ))
        )}
      </Box>
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEditAddress}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteAddress}>Delete</MenuItem>
      </Menu>
    </Box>
  );
};

export default SavedAddresses;

